import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SavedSignature } from '../types/fields';
import {
  loadSavedSignatures,
  saveSignaturesToStorage,
  upsertSavedSignature,
} from '../utils/signatureLibraryStorage';

const STORAGE_ERROR_MESSAGE_PLACED_ONCE =
  'Could not save signature to your library (storage may be full). It has been placed for this session only.';

const MAX_SIGNATURE_LABEL_LENGTH = 60;
const STORAGE_ERROR_MESSAGE =
  'Could not save signature locally (storage may be full). You can still place it this session.';

export interface PendingSignaturePlacement {
  dataUrl: string;
  savedId?: string;
  label: string;
  source: 'use-once' | 'saved';
}

function normalizePlacementLabel(label: string, fallback = 'Signature'): string {
  const normalized = label.trim().slice(0, MAX_SIGNATURE_LABEL_LENGTH);
  return normalized || fallback;
}

export function useSignatureLibrary() {
  // Hydrate synchronously so the library is available on first render (no flash).
  const [savedSignatures, setSavedSignatures] = useState<SavedSignature[]>(() => loadSavedSignatures());
  const [pendingPlacement, setPendingPlacement] = useState<PendingSignaturePlacement | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);

  // Synchronous source of truth so two mutations in one tick (rapid double-click,
  // rename + delete) each read the latest list instead of a stale closure and
  // clobbering one another.
  const savedRef = useRef<SavedSignature[]>(savedSignatures);
  const hydrated = useRef(false);

  // Persist on change. Skips the initial hydration value (writing back what we just
  // read is pointless) and surfaces quota failures instead of silently dropping them.
  useEffect(() => {
    savedRef.current = savedSignatures;
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    try {
      saveSignaturesToStorage(savedSignatures);
      setStorageError(null);
    } catch {
      setStorageError(STORAGE_ERROR_MESSAGE);
    }
  }, [savedSignatures]);

  const commitSaved = useCallback((next: SavedSignature[]) => {
    savedRef.current = next;
    setSavedSignatures(next);
  }, []);

  const saveAndPlace = useCallback(
    (dataUrl: string, label: string) => {
      // Compute the upsert exactly once against the latest committed list, so the
      // stored array and pendingPlacement reference the SAME entry id, and a fast
      // double-click can't create a duplicate (the second call sees the first insert).
      const result = upsertSavedSignature(savedRef.current, dataUrl, label);

      // F23: persist BEFORE committing state, so a quota failure cannot produce a
      // "ghost" signature — a pendingPlacement with source 'saved' referencing a
      // savedId that exists only in React state and vanishes on refresh. On failure
      // we keep the library unchanged and place the signature for this session only.
      try {
        saveSignaturesToStorage(result.signatures);
      } catch {
        setStorageError(STORAGE_ERROR_MESSAGE_PLACED_ONCE);
        setPendingPlacement({
          dataUrl: result.signature.dataUrl,
          label: result.signature.label,
          source: 'use-once',
        });
        return;
      }

      setStorageError(null);
      commitSaved(result.signatures);
      setPendingPlacement({
        dataUrl: result.signature.dataUrl,
        savedId: result.signature.id,
        label: result.signature.label,
        source: 'saved',
      });
    },
    [commitSaved],
  );

  const useOnce = useCallback((dataUrl: string, label: string) => {
    setPendingPlacement({
      dataUrl,
      label: normalizePlacementLabel(label),
      source: 'use-once',
    });
  }, []);

  const placeSavedSignature = useCallback((signature: SavedSignature) => {
    setPendingPlacement({
      dataUrl: signature.dataUrl,
      savedId: signature.id,
      label: signature.label,
      source: 'saved',
    });
  }, []);

  const renameSavedSignature = useCallback(
    (id: string, label: string) => {
      const normalizedLabel = label.trim().slice(0, MAX_SIGNATURE_LABEL_LENGTH);
      if (!normalizedLabel) {
        return;
      }

      const current = savedRef.current;
      let didRename = false;
      const next = current.map((signature) => {
        if (signature.id !== id) {
          return signature;
        }
        didRename = true;
        return { ...signature, label: normalizedLabel };
      });

      if (didRename) {
        commitSaved(next);
      }
    },
    [commitSaved],
  );

  const deleteSavedSignature = useCallback(
    (id: string) => {
      const current = savedRef.current;
      const next = current.filter((signature) => signature.id !== id);
      if (next.length !== current.length) {
        commitSaved(next);
      }
    },
    [commitSaved],
  );

  const clearPendingPlacement = useCallback(() => {
    setPendingPlacement(null);
  }, []);

  const clearStorageError = useCallback(() => {
    setStorageError(null);
  }, []);

  return useMemo(
    () => ({
      savedSignatures,
      pendingPlacement,
      storageError,
      saveAndPlace,
      useOnce,
      placeSavedSignature,
      renameSavedSignature,
      deleteSavedSignature,
      clearPendingPlacement,
      clearStorageError,
    }),
    [
      savedSignatures,
      pendingPlacement,
      storageError,
      saveAndPlace,
      useOnce,
      placeSavedSignature,
      renameSavedSignature,
      deleteSavedSignature,
      clearPendingPlacement,
      clearStorageError,
    ],
  );
}
